import { ProCard, ProDescriptions } from '@ant-design/pro-components'
import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { resolveArticleCategories } from '@app/icare/db/fixture/article_category'
import { EconomicValueReasonTypeEnum } from '@app/icare/entities/article/schema/Article'
import { ArticleEditDrawer } from '@app/icare/routes/_app/artikel/-components/ArticleEditDrawer'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { BooleanStatusBadge } from '@pkg/ui/badge'
import { EditSectionButton } from '@pkg/ui/button'
import { TypedDescriptionsItem } from '@pkg/ui/proValueTypes'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Flex, Tag } from 'antd'

export const Route = createFileRoute('/_app/artikel/$id/')({
  component: RouteComponent,
})

function RouteComponent() {
  const params = Route.useParams()
  const { data } = useQuery(localQueryEntityOptions(params.id))

  if (!data) {
    return <ProCard loading />
  }

  const article = data
  const categories = resolveArticleCategories(article.categoryIds)
  const economicValues = article.economicValue?.reason ?? []

  return (
    <Flex vertical gap="small" className="mx-4">
      <ProCard
        headerBordered
        title={t`Allgemein`}
        extra={
          <EditSectionButton onClick={() => void ArticleEditDrawer.show({ id: article.id, section: 'general' })} />
        }
      >
        <ProDescriptions column={1} size="small" colon={false}>
          <TypedDescriptionsItem label={t`Name`}>{article.name}</TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Technischer Name`}>{article.details?.technicalName}</TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Ressourcennummer`}>{article.details?.resourceNumber}</TypedDescriptionsItem>
        </ProDescriptions>
      </ProCard>

      <ProCard
        headerBordered
        title={t`Bestand`}
        extra={
          <EditSectionButton onClick={() => void ArticleEditDrawer.show({ id: article.id, section: 'inventory' })} />
        }
      >
        <ProDescriptions column={1} size="small" colon={false}>
          <TypedDescriptionsItem label={t`Verfügbar`}>
            <BooleanStatusBadge status={!!article.inventory?.available} />
          </TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Auf Lager`}>{article.inventory?.onStock}</TypedDescriptionsItem>
        </ProDescriptions>
      </ProCard>

      <ProCard
        headerBordered
        title={t`Verpackung`}
        extra={
          <EditSectionButton onClick={() => void ArticleEditDrawer.show({ id: article.id, section: 'packaging' })} />
        }
      >
        <ProDescriptions column={1} size="small" colon={false}>
          <TypedDescriptionsItem label={t`Menge`}>{article.packaging?.amount}</TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Anzahl der Einheiten`}>
            {article.packaging?.numberOfUnits}
          </TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Maßeinheit`}>{article.packaging?.unitOfMeasurement}</TypedDescriptionsItem>
        </ProDescriptions>
      </ProCard>

      <ProCard
        headerBordered
        title={t`Abrechnung`}
        extra={
          <EditSectionButton onClick={() => void ArticleEditDrawer.show({ id: article.id, section: 'pricing' })} />
        }
      >
        <ProDescriptions column={1} size="small" colon={false}>
          <TypedDescriptionsItem label={t`Preis pro Einheit`}>{article.pricing?.pricePerUnit}</TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Abrechnung nach`}>
            {article.pricing?.billingAccordingTo}
          </TypedDescriptionsItem>
        </ProDescriptions>
      </ProCard>

      <ProCard
        headerBordered
        title={t`Klassifizierung`}
        extra={
          <EditSectionButton
            onClick={() => void ArticleEditDrawer.show({ id: article.id, section: 'classification' })}
          />
        }
      >
        <ProDescriptions column={1} size="small" colon={false}>
          <TypedDescriptionsItem label={t`Kategorien`}>
            {categories.length > 0 ? (
              categories.map(category => <Tag key={category.id}>{category.name}</Tag>)
            ) : (
              <span className="text-gray-400">
                <Trans>Keine Angabe</Trans>
              </span>
            )}
          </TypedDescriptionsItem>
          <TypedDescriptionsItem label={t`Wirtschaftlicher Wert`}>
            {economicValues.length > 0 ? (
              economicValues.map(reason => (
                <Tag key={reason.value}>{EconomicValueReasonTypeEnum.$.getLabel(reason.value)}</Tag>
              ))
            ) : (
              <span className="text-gray-400">
                <Trans>Keine Angabe</Trans>
              </span>
            )}
          </TypedDescriptionsItem>
        </ProDescriptions>
      </ProCard>
    </Flex>
  )
}
